import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Skeleton from '@mui/material/Skeleton';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { SettlementExportRendererKey } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useState } from 'react';

import useCompanyExportRenderers from '~hooks/useCompanyExportRenderers/useCompanyExportRenderers';
import useCompanyExportRenderersQuery, {
  companyExportRenderersQueryKey,
} from '~queries/useCompanyExportRenderersQuery';
import { getSettlementExportRendererLabel } from '~utils/settlements/settlement-export-utils';

// The list endpoint returns only the renderers a company has enabled, so the full set
// of toggles comes from the spec's renderer registry. Each key states whether a Platform
// Admin can assign it, so a renderer the API adds fails type-check until it's decided.
const rendererIsAssignable = {
  [SettlementExportRendererKey.SAGE_PAYABLE]: true,
  [SettlementExportRendererKey.SAGE_RECEIVABLE]: true,
} satisfies Record<SettlementExportRendererKey, boolean>;

const ASSIGNABLE_RENDERER_KEYS = Object.values(SettlementExportRendererKey).filter(
  (rendererKey) => rendererIsAssignable[rendererKey],
);

interface CompanyExportRendererTogglesProps {
  companyId: string;
}

export default function CompanyExportRendererToggles({
  companyId,
}: CompanyExportRendererTogglesProps) {
  const queryClient = useQueryClient();
  const { companyExportRenderers, isError, isPending } = useCompanyExportRenderersQuery({
    companyId,
  });
  const { createCompanyExportRenderer, deleteCompanyExportRenderer } =
    useCompanyExportRenderers();
  const [updatingRendererKeys, setUpdatingRendererKeys] = useState<
    SettlementExportRendererKey[]
  >([]);

  const handleToggle = useCallback(
    async (rendererKey: SettlementExportRendererKey, shouldEnable: boolean) => {
      setUpdatingRendererKeys((keys) => [...keys, rendererKey]);

      try {
        const enabledRenderer = companyExportRenderers.find(
          (companyExportRenderer) => companyExportRenderer.rendererKey === rendererKey,
        );

        if (shouldEnable) {
          await createCompanyExportRenderer({ companyId, rendererKey });
        } else if (enabledRenderer) {
          await deleteCompanyExportRenderer({
            companyId,
            companyExportRendererId: enabledRenderer.id,
          });
        }

        await queryClient.invalidateQueries({
          queryKey: companyExportRenderersQueryKey(companyId),
        });
      } catch {
        // Failure already surfaced by the hooks; the switch just stays unchanged.
      } finally {
        setUpdatingRendererKeys((keys) => keys.filter((key) => key !== rendererKey));
      }
    },
    [
      companyExportRenderers,
      companyId,
      createCompanyExportRenderer,
      deleteCompanyExportRenderer,
      queryClient,
    ],
  );

  if (isPending) {
    return <Skeleton height={80} variant="rectangular" width={540} />;
  }

  // Without the enabled list every switch would read "off", and turning one on could
  // duplicate a renderer the company already has.
  if (isError) {
    return (
      <Alert data-test-id="export-renderers-fetch-error" severity="error">
        {t('error_messages.company_export_renderers.failed_to_fetch')}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="h6">{t('administration.export_renderers.title')}</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('administration.export_renderers.description')}
      </Typography>

      {ASSIGNABLE_RENDERER_KEYS.map((rendererKey) => {
        const isEnabled = companyExportRenderers.some(
          (companyExportRenderer) => companyExportRenderer.rendererKey === rendererKey,
        );
        const isUpdating = updatingRendererKeys.includes(rendererKey);

        return (
          <FormControlLabel
            control={
              <Switch
                checked={isEnabled}
                data-test-id={`export-renderer-toggle-${rendererKey}`}
                disabled={isUpdating}
                onChange={(_event, checked) => void handleToggle(rendererKey, checked)}
              />
            }
            key={rendererKey}
            label={getSettlementExportRendererLabel(rendererKey)}
          />
        );
      })}
    </Box>
  );
}
